import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useUserSettings } from '../contexts/UserSettingsContext';
import { useAchievements } from '../contexts/AchievementContext';
import apiService from '../services/api';
import useFormStorage from '../hooks/useFormStorage';
import HelpDisclosure from '../components/HelpDisclosure';
import ShareModal from '../components/ShareModal';
import ShoppingListGenerator from '../components/ShoppingListGenerator';
import { requireAuth } from '../utils/authPrompt';
import { createLocalDate, getDayOfWeek, getLocalDateString } from '../utils/dateUtils';
import {
  compareRecipeIngredientToPantry,
  normalizeIngredientName as normalizePantryIngredientName,
  ingredientsMatch as pantryIngredientsMatch
} from '../utils/pantryQuantity';
import '../styles/Recipes.css';
import '../styles/ImageUpload.css';

const recipeFormHelp = [
  {
    question: "Which fields are required?",
    answer: "Recipe name, description, difficulty, category, prep time, cook time, ingredients, and instructions are required. Image, tags, cuisine, and nutrition are optional."
  },
  {
    question: "How should I write ingredients?",
    answer: "Put one ingredient on each line. Amounts are optional, but examples like 2 cups flour or 1 onion make the recipe clearer."
  },
  {
    question: "Can I save without a photo?",
    answer: "Yes. You can upload a photo, paste an image URL, or leave the recipe without an image."
  }
];

const Recipes = ({ sidebarOpen }) => {
  const { user } = useUser();
  const { settings } = useUserSettings();
  const { handleApiResponse, isShowingAchievement } = useAchievements();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [showMyRecipes, setShowMyRecipes] = useState(false);
  const [showMyFavorites, setShowMyFavorites] = useState(false);
  const [showPantryRecipes, setShowPantryRecipes] = useState(false);
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [showIngredientFilter, setShowIngredientFilter] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showShoppingListGenerator, setShowShoppingListGenerator] = useState(false);
  const [selectedRecipesForShopping, setSelectedRecipesForShopping] = useState([]);
  const [showMealPlanModal, setShowMealPlanModal] = useState(false);
  const [mealPlanTargetRecipe, setMealPlanTargetRecipe] = useState(null);
  const [mealPlanChoices, setMealPlanChoices] = useState([]);
  const [mealPlanSelection, setMealPlanSelection] = useState({
    planId: '',
    date: getLocalDateString(new Date()),
    mealType: 'dinner'
  });
  const [pantryItems, setPantryItems] = useState([]);
  const [recipePortions, setRecipePortions] = useState({});
  const [recipeMissingIngredients, setRecipeMissingIngredients] = useState({});
  const [showMissingIngredientsModal, setShowMissingIngredientsModal] = useState(false);
  const [selectedMissingIngredients, setSelectedMissingIngredients] = useState({ recipeName: '', ingredients: [] });

  // Temporary storage for recipe form data
  const {
    formData: storedFormData,
    updateFormData,
    clearStoredData,
    hasStoredData
  } = useFormStorage('recipe-form-draft', {
    title: '',
    description: '',
    ingredients: '',
    instructions: '',
    difficulty: '',
    category: 'Dinner',
    cuisine: '',
    prepTime: '',
    cookTime: '',
    servings: '4',
    imageUrl: '',
    tags: '',
    calories: '',
    fat: '',
    carbs: '',
    protein: ''
  });

  // Fetch recipes from database
  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch public recipes from database
        const response = await apiService.request('/recipes');
        
        // Apply frontend prioritization if user has preferences and setting is enabled
        let prioritizedRecipes = response;
        if (user && settings.prioritizeRecipes) {
          const userPreferences = {
            favoriteCuisine: user.favoriteCuisine,
            cookingExperience: user.cookingExperience
          };
          prioritizedRecipes = prioritizeRecipes(response, userPreferences);
        }
        
        setRecipes(prioritizedRecipes);
        setFilteredRecipes(prioritizedRecipes);
      } catch (error) {
        console.error('Error fetching recipes:', error);
        setError('Failed to load recipes. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecipes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, settings.prioritizeRecipes]);

  // Fetch pantry items when user is logged in
  useEffect(() => {
    const fetchPantryItems = async () => {
      if (!user) {
        setPantryItems([]);
        return;
      }

      try {
        const response = await apiService.request('/pantry');
        setPantryItems(response.items || []);
      } catch (error) {
        console.error('Error fetching pantry items:', error);
      }
    };

    fetchPantryItems();
  }, [user]);

  // Fetch user's recipes when "My Recipes" is toggled
  useEffect(() => {
    const fetchUserRecipes = async () => {
      if (showMyRecipes && user) {
        try {
          setIsLoading(true);
          setError(null);
          
          // Fetch user's own recipes
          const userRecipes = await apiService.getUserRecipes();
          setRecipes(userRecipes);
          setFilteredRecipes(userRecipes);
        } catch (error) {
          console.error('Error fetching user recipes:', error);
          setError('Failed to load your recipes. Please try again.');
        } finally {
          setIsLoading(false);
        }
      } else if (!showMyRecipes && !showMyFavorites) {
        // Reset to all public recipes only if favorites is also not selected
        const fetchAllRecipes = async () => {
          try {
            setIsLoading(true);
            setError(null);
            
            const response = await apiService.request('/recipes');
            setRecipes(response);
            setFilteredRecipes(response);
          } catch (error) {
            console.error('Error fetching recipes:', error);
            setError('Failed to load recipes. Please try again.');
          } finally {
            setIsLoading(false);
          }
        };
        
        fetchAllRecipes();
      }
    };

    fetchUserRecipes();
  }, [showMyRecipes, showMyFavorites, user]);

  // Fetch user's favorite recipes when "My Favorites" is toggled
  useEffect(() => {
    const fetchFavoriteRecipes = async () => {
      if (showMyFavorites && user) {
        try {
          setIsLoading(true);
          setError(null);
          
          // Fetch user's favorite recipes
          const favoriteRecipes = await apiService.getFavoriteRecipes();
          setRecipes(favoriteRecipes);
          setFilteredRecipes(favoriteRecipes);
        } catch (error) {
          console.error('Error fetching favorite recipes:', error);
          setError('Failed to load your favorite recipes. Please try again.');
        } finally {
          setIsLoading(false);
        }
      } else if (!showMyFavorites && !showMyRecipes) {
        // Reset to all public recipes only if my recipes is also not selected
        const fetchAllRecipes = async () => {
          try {
            setIsLoading(true);
            setError(null);
            
            const response = await apiService.request('/recipes');
            setRecipes(response);
            setFilteredRecipes(response);
          } catch (error) {
            console.error('Error fetching recipes:', error);
            setError('Failed to load recipes. Please try again.');
          } finally {
            setIsLoading(false);
          }
        };
        
        fetchAllRecipes();
      }
    };

    fetchFavoriteRecipes();
  }, [showMyFavorites, showMyRecipes, user]);

  // Helper function to normalize ingredient names for matching
  const normalizeIngredientName = (name) => {
    return normalizePantryIngredientName(name);
  };

  // Check if two ingredient names match
  const ingredientsMatch = (name1, name2) => {
    return pantryIngredientsMatch(name1, name2);
  };

  // Check pantry availability and calculate portions
  const checkPantryAvailability = (recipe) => {
    if (!recipe.ingredients || !Array.isArray(recipe.ingredients) || recipe.ingredients.length === 0) {
      return { canMake: false, portions: 0, missingIngredients: [] };
    }

    let minPortions = Infinity;
    let matchedIngredients = 0;
    let totalIngredients = 0;
    const missingIngredients = [];

    for (const recipeIngredient of recipe.ingredients) {
      const recipeName = normalizeIngredientName(recipeIngredient);
      
      if (!recipeName) continue;
      
      totalIngredients++;

      // Find matching pantry item
      const matchingPantryItems = pantryItems.filter(item => 
        ingredientsMatch(item.name, recipeIngredient)
      );

      if (matchingPantryItems.length > 0) {
        matchedIngredients++;

        let matchedEnough = false;
        let bestShortage = '';
        let bestPortions = 1;

        for (const pantryItem of matchingPantryItems) {
          const comparison = compareRecipeIngredientToPantry(recipeIngredient, pantryItem);

          if (comparison.hasEnough) {
            matchedEnough = true;
            bestPortions = comparison.comparable && comparison.portions >= 1
              ? Math.max(bestPortions, comparison.portions)
              : Math.max(bestPortions, 1);
            break;
          }

          if (!bestShortage && comparison.shortageText) {
            bestShortage = comparison.shortageText;
          }
        }

        if (matchedEnough) {
          minPortions = Math.min(minPortions, bestPortions);
        } else if (bestShortage) {
          missingIngredients.push(bestShortage);
        } else {
          missingIngredients.push(recipeIngredient);
        }
      } else {
        missingIngredients.push(recipeIngredient);
      }
    }

    // Show recipe if at least 3 ingredients are available (even partially)
    const canMake = matchedIngredients >= 3;
    const portions = (canMake && missingIngredients.length === 0) 
      ? (minPortions === Infinity ? 1 : minPortions) 
      : 0;
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`Recipe "${recipe.title}": matched ${matchedIngredients}/${totalIngredients} ingredients, canMake: ${canMake}, portions: ${portions}, missing/short: ${missingIngredients.length}`);
    }
    
    return { canMake, portions, missingIngredients };
  };

  // Calculate portions and missing ingredients for all recipes when pantry filter is active
  useEffect(() => {
    if (showPantryRecipes && pantryItems.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('=== Pantry Filter Active ===');
        console.log(`Pantry has ${pantryItems.length} items:`, pantryItems.map(item => `${item.name} (${item.quantity})`));
        console.log(`Checking ${recipes.length} recipes...`);
      }
      
      const portions = {};
      const missingIngredients = {};
      
      recipes.forEach(recipe => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`\n--- Checking recipe: "${recipe.title}" ---`);
          console.log('Recipe ingredients:', recipe.ingredients);
        }
        const result = checkPantryAvailability(recipe);
        portions[recipe.id] = result.portions;
        missingIngredients[recipe.id] = result.missingIngredients;
      });
      
      setRecipePortions(portions);
      setRecipeMissingIngredients(missingIngredients);
      
      if (process.env.NODE_ENV === 'development') {
        const availableRecipes = recipes.filter(r => {
          const result = checkPantryAvailability(r);
          return result.canMake;
        });
        console.log(`\n=== Summary: ${availableRecipes.length} recipes available with at least 3 ingredients ===`);
      }
    } else {
      setRecipePortions({});
      setRecipeMissingIngredients({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPantryRecipes, recipes, pantryItems]);

  // Filter recipes based on search and filters
  useEffect(() => {
    let filtered = recipes;

    if (searchTerm) {
      filtered = filtered.filter(recipe =>
        recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipe.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (recipe.tags && recipe.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    if (selectedDifficulty) {
      filtered = filtered.filter(recipe => recipe.difficulty === selectedDifficulty);
    }

    if (selectedCuisine) {
      filtered = filtered.filter(recipe => recipe.cuisine === selectedCuisine);
    }

    if (selectedCategory && selectedCategory !== 'All') {
      filtered = filtered.filter(recipe => recipe.category === selectedCategory);
    }

    if (selectedTime) {
      filtered = filtered.filter(recipe => {
        const totalTime = parseInt(recipe.prepTime) + parseInt(recipe.cookTime);
        switch (selectedTime) {
          case 'quick': return totalTime <= 30;
          case 'medium': return totalTime > 30 && totalTime <= 60;
          case 'long': return totalTime > 60;
          default: return true;
        }
      });
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(recipe => 
        recipe.tags && selectedTags.some(selectedTagItem => 
          recipe.tags.some(tag => tag.toLowerCase() === selectedTagItem.toLowerCase())
        )
      );
    }

    if (selectedIngredients.length > 0) {
      filtered = filtered.filter(recipe => {
        if (!recipe.ingredients || !Array.isArray(recipe.ingredients)) return false;
        
        // Check if recipe contains any of the selected ingredients
        return selectedIngredients.some(selectedIngredient => 
          recipe.ingredients.some(ingredient => 
            ingredient.toLowerCase().includes(selectedIngredient.toLowerCase())
          )
        );
      });
    }

    // Filter by pantry availability (at least 3 ingredients)
    if (showPantryRecipes) {
      if (pantryItems.length === 0) {
        // If pantry is empty, show no recipes
        filtered = [];
      } else {
        // If pantry has items, show recipes with at least 3 matching ingredients
        filtered = filtered.filter(recipe => {
          const result = checkPantryAvailability(recipe);
          return result.canMake;
        });
      }
    }

    setFilteredRecipes(filtered);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedDifficulty, selectedCuisine, selectedCategory, selectedTime, selectedTags, selectedIngredients, recipes, showPantryRecipes, pantryItems]);


  // Show success message
  const showSuccessMessage = (message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccessMessage(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  // Get top 6 most used tags from recipes
  const getTopTags = () => {
    const tagCounts = {};
    recipes.forEach(recipe => {
      if (recipe.tags && Array.isArray(recipe.tags)) {
        recipe.tags.forEach(tag => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });
    
    // Sort by count (descending) and take top 6
    return Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 6)
      .map(([tag]) => tag);
  };

  // Clear search and filters
  const clearSearchAndFilters = () => {
    setSearchTerm('');
    setSelectedDifficulty('');
    setSelectedCuisine('');
    setSelectedCategory('All');
    setSelectedTime('');
    setSelectedTags([]);
    setSelectedIngredients([]);
    setShowPantryRecipes(false);
  };

  // Handle tag selection/deselection
  const toggleTag = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Handle ingredient selection/deselection
  const toggleIngredient = (ingredient) => {
    setSelectedIngredients(prev => {
      if (prev.includes(ingredient)) {
        return prev.filter(i => i !== ingredient);
      } else {
        return [...prev, ingredient];
      }
    });
  };

  // Comprehensive ingredient list
  const getIngredientList = () => {
    return [
      // Proteins
      'Chicken', 'Beef', 'Pork', 'Fish', 'Salmon', 'Tuna', 'Shrimp', 'Crab', 'Lobster', 'Turkey', 'Duck', 'Lamb', 'Bacon', 'Sausage', 'Ham', 'Eggs',
      // Dairy
      'Milk', 'Cheese', 'Cheddar', 'Mozzarella', 'Parmesan', 'Cream', 'Butter', 'Yogurt', 'Sour Cream', 'Cream Cheese',
      // Vegetables
      'Onions', 'Garlic', 'Tomatoes', 'Potatoes', 'Carrots', 'Broccoli', 'Spinach', 'Lettuce', 'Bell Peppers', 'Mushrooms', 'Zucchini', 'Eggplant', 'Cucumber', 'Celery', 'Cabbage', 'Cauliflower', 'Asparagus', 'Green Beans', 'Corn', 'Peas',
      // Fruits
      'Apples', 'Bananas', 'Oranges', 'Lemons', 'Limes', 'Berries', 'Strawberries', 'Blueberries', 'Raspberries', 'Grapes', 'Avocado', 'Pineapple', 'Mango', 'Peaches', 'Pears',
      // Grains & Starches
      'Rice', 'Pasta', 'Bread', 'Flour', 'Oats', 'Quinoa', 'Barley', 'Couscous', 'Noodles', 'Tortillas', 'Crackers', 'Cereal',
      // Pantry Staples
      'Olive Oil', 'Vegetable Oil', 'Salt', 'Pepper', 'Sugar', 'Brown Sugar', 'Honey', 'Vinegar', 'Soy Sauce', 'Worcestershire Sauce', 'Ketchup', 'Mustard', 'Mayonnaise', 'Hot Sauce', 'Sesame Oil', 'Coconut Oil',
      // Spices & Herbs
      'Basil', 'Oregano', 'Thyme', 'Rosemary', 'Parsley', 'Cilantro', 'Dill', 'Chives', 'Cinnamon', 'Nutmeg', 'Ginger', 'Garlic Powder', 'Onion Powder', 'Paprika', 'Cumin', 'Coriander', 'Bay Leaves', 'Red Pepper Flakes', 'Black Pepper', 'Sea Salt',
      // Canned & Jarred
      'Tomato Sauce', 'Tomato Paste', 'Coconut Milk', 'Chicken Broth', 'Beef Broth', 'Vegetable Broth', 'Beans', 'Chickpeas', 'Lentils', 'Olives', 'Pickles', 'Capers', 'Sun-dried Tomatoes',
      // Nuts & Seeds
      'Almonds', 'Walnuts', 'Pecans', 'Cashews', 'Pistachios', 'Pine Nuts', 'Sesame Seeds', 'Chia Seeds', 'Flax Seeds', 'Peanuts', 'Peanut Butter', 'Almond Butter',
      // Baking
      'Baking Powder', 'Baking Soda', 'Vanilla Extract', 'Chocolate', 'Cocoa Powder', 'Powdered Sugar', 'Cornstarch', 'Yeast', 'Shortening'
    ];
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showSuccessMessage('Please select a valid image file.');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      showSuccessMessage('Image size must be less than 5MB.');
      return;
    }

    try {
      setIsUploading(true);
      const response = await apiService.uploadImage(file);
      setUploadedImage(response.imageUrl);
      showSuccessMessage('Image uploaded successfully!');
      
      // Clear the URL input field when image is uploaded
      const urlInput = document.querySelector('input[name="imageUrl"]');
      if (urlInput) {
        urlInput.value = '';
        urlInput.placeholder = 'Image uploaded - URL field disabled';
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showSuccessMessage('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Clear uploaded image
  const clearUploadedImage = () => {
    setUploadedImage(null);
    // Reset the URL input field
    const urlInput = document.querySelector('input[name="imageUrl"]');
    if (urlInput) {
      urlInput.value = '';
      urlInput.placeholder = 'https://example.com/image.jpg';
    }
  };

  // Handle recipe card click
  const handleRecipeClick = (recipeId) => {
    navigate(`/recipes/${recipeId}`);
  };

  // Handle share button click
  const handleShareClick = (e, recipe) => {
    e.stopPropagation(); // Prevent card click
    setSelectedRecipe(recipe);
    setShowShareModal(true);
  };

  // Handle shopping list generation
  const handleGenerateShoppingList = (recipe) => {
    if (!requireAuth(user, "Register or log in to make shopping lists from recipes.")) return;
    setSelectedRecipesForShopping([recipe]);
    setShowShoppingListGenerator(true);
  };

  const openMealPlanModal = async (recipe) => {
    if (!requireAuth(user, "Register or log in to add recipes to meal plans.")) return;

    setMealPlanTargetRecipe(recipe);
    setShowMealPlanModal(true);

    try {
      const response = await apiService.getMealPlans();
      const plans = response?.plans || [];
      setMealPlanChoices(plans);
      setMealPlanSelection((prev) => ({
        ...prev,
        planId: prev.planId || plans[0]?.id || '',
        mealType: ['breakfast', 'lunch', 'dinner', 'snack'].includes(String(recipe.category || '').toLowerCase())
          ? String(recipe.category).toLowerCase()
          : prev.mealType
      }));
    } catch (error) {
      console.error('Failed to load meal plans:', error);
      showSuccessMessage('Failed to load meal plans. Please try again.');
    }
  };

  const handleAddRecipeToMealPlan = async (e) => {
    e.preventDefault();

    if (!mealPlanTargetRecipe || !mealPlanSelection.planId) {
      showSuccessMessage('Create or select a meal plan first.');
      return;
    }

    try {
      const selectedDate = mealPlanSelection.date || getLocalDateString(new Date());
      const dateObj = createLocalDate(selectedDate);
      const totalTime = (Number(mealPlanTargetRecipe.prepTime) || 0) + (Number(mealPlanTargetRecipe.cookTime) || 0);
      const ingredients = Array.isArray(mealPlanTargetRecipe.ingredients)
        ? mealPlanTargetRecipe.ingredients.slice(0, 6)
        : [];

      const mealData = {
        mealName: mealPlanTargetRecipe.title,
        mealType: mealPlanSelection.mealType || 'dinner',
        dayOfWeek: getDayOfWeek(dateObj),
        date: selectedDate,
        description: String(mealPlanTargetRecipe.description || '').slice(0, 140),
        calories: Number(mealPlanTargetRecipe.calories) || 0,
        notes: [
          `Added from recipe: ${mealPlanTargetRecipe.title}`,
          totalTime > 0 ? `${totalTime} min total` : '',
          ingredients.length > 0 ? `Ingredients: ${ingredients.join(', ')}${(mealPlanTargetRecipe.ingredients || []).length > ingredients.length ? '...' : ''}` : ''
        ].filter(Boolean).join('\n')
      };

      const response = await apiService.addMealPlanMeal(mealPlanSelection.planId, mealData);
      handleApiResponse(response);
      showSuccessMessage('Recipe added to meal plan!');
      setShowMealPlanModal(false);
      setMealPlanTargetRecipe(null);
    } catch (error) {
      console.error('Failed to add recipe to meal plan:', error);
      showSuccessMessage('Failed to add recipe to meal plan.');
    }
  };

  // Handle shopping list generation for multiple recipes
  // const handleGenerateShoppingListForMultiple = () => {
  //   if (selectedRecipesForShopping.length === 0) {
  //     showSuccessMessage('Please select at least one recipe to generate a shopping list.');
  //     return;
  //   }
  //   setShowShoppingListGenerator(true);
  // };

  // Handle shopping list created
  const handleShoppingListCreated = (shoppingList) => {
    showSuccessMessage(`Shopping list "${shoppingList.name}" created successfully!`);
    setShowShoppingListGenerator(false);
    setSelectedRecipesForShopping([]);
  };

  // Handle recipe like/unlike
  const handleLikeRecipe = async (recipe) => {
    if (!requireAuth(user, "Register or log in to save favorite recipes.")) return;

    try {
      const response = await apiService.likeRecipe(recipe.id);
      
      // Handle achievement notifications
      handleApiResponse(response);
      
      // Update the recipe in the current recipes list
      setRecipes(prevRecipes => 
        prevRecipes.map(r => 
          r.id === recipe.id 
            ? { ...r, isLiked: response.isLiked, likes: response.likesCount }
            : r
        )
      );
      
      // Update filtered recipes as well
      setFilteredRecipes(prevRecipes => 
        prevRecipes.map(r => 
          r.id === recipe.id 
            ? { ...r, isLiked: response.isLiked, likes: response.likesCount }
            : r
        )
      );

      // If we're viewing favorites and the recipe was unliked, remove it from the list
      if (showMyFavorites && !response.isLiked) {
        setRecipes(prevRecipes => prevRecipes.filter(r => r.id !== recipe.id));
        setFilteredRecipes(prevRecipes => prevRecipes.filter(r => r.id !== recipe.id));
      }
      
      showSuccessMessage(response.isLiked ? 'Recipe added to favorites!' : 'Recipe removed from favorites!');
    } catch (error) {
      console.error('Error liking recipe:', error);
      showSuccessMessage('Failed to update recipe. Please try again.');
    }
  };

  // Handle form input changes for temporary storage
  const handleFormInputChange = (fieldName, value) => {
    updateFormData({ [fieldName]: value });
  };


  // Clear form data and storage
  const clearFormData = () => {
    clearStoredData();
    setUploadedImage(null);
    const form = document.getElementById('add-recipe-form');
    if (form) {
      form.reset();
    }
    showSuccessMessage('Form data cleared!');
  };

  // Handle show missing ingredients modal
  const handleShowMissingIngredients = (recipeName, missingIngredients) => {
    setSelectedMissingIngredients({ recipeName, ingredients: missingIngredients });
    setShowMissingIngredientsModal(true);
  };

  // Handle add recipe
  const handleAddRecipe = async (recipeData) => {
    try {
      const newRecipe = await apiService.createRecipe(recipeData);
      setRecipes(prev => [newRecipe, ...prev]);
      setFilteredRecipes(prev => [newRecipe, ...prev]);
      showSuccessMessage('Recipe added successfully!');
      setShowAddModal(false);
      setUploadedImage(null);
      // Clear stored data after successful submission
      clearStoredData();
    } catch (error) {
      console.error('Error adding recipe:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to add recipe. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Please login first to add recipes.';
      } else if (error.response?.status === 400) {
        const errorData = error.response.data;
        if (errorData?.details && Array.isArray(errorData.details)) {
          // Show specific validation errors
          errorMessage = `Validation errors: ${errorData.details.join(', ')}`;
        } else {
          errorMessage = errorData?.message || 'Please check all required fields.';
        }
      } else if (error.response?.status === 403) {
        errorMessage = 'Not authorized to add recipes.';
      } else if (error.response?.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      showSuccessMessage(errorMessage);
    }
  };

  // Function to prioritize recipes based on user preferences
  const prioritizeRecipes = (recipes, userPreferences) => {
    if (!userPreferences || !settings.prioritizeRecipes) {
      return recipes;
    }

    const { favoriteCuisine, cookingExperience } = userPreferences;
    
    // Map cooking experience to difficulty levels
    const experienceToDifficulty = {
      'Beginner': ['easy'],
      'Intermediate': ['easy', 'medium'],
      'Advanced': ['medium', 'hard'],
      'Expert': ['easy', 'medium', 'hard']
    };
    
    const preferredDifficulties = experienceToDifficulty[cookingExperience] || ['easy', 'medium', 'hard'];
    
    return recipes.sort((a, b) => {
      // Calculate priority scores
      const aScore = calculatePriorityScore(a, favoriteCuisine, preferredDifficulties);
      const bScore = calculatePriorityScore(b, favoriteCuisine, preferredDifficulties);
      
      // Higher score = higher priority
      return bScore - aScore;
    });
  };

  // Helper function to calculate priority score
  const calculatePriorityScore = (recipe, favoriteCuisine, preferredDifficulties) => {
    let score = 0;
    
    // Cuisine match (higher weight)
    if (recipe.cuisine === favoriteCuisine) {
      score += 100;
    }
    
    // Difficulty match
    if (preferredDifficulties.includes(recipe.difficulty)) {
      score += 50;
    }
    
    // Both cuisine and difficulty match (bonus)
    if (recipe.cuisine === favoriteCuisine && preferredDifficulties.includes(recipe.difficulty)) {
      score += 200;
    }
    
    // Recent recipes get slight boost
    const daysSinceCreation = (new Date() - new Date(recipe.createdAt)) / (1000 * 60 * 60 * 24);
    score += Math.max(0, 10 - daysSinceCreation);
    
    return score;
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'hard': return '#ef4444';
      default: return '#6b7280';
    }
  };


  return (
    <div className={`recipes-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Header */}
      <div className="recipes-header">
        <div>
          <h1 className="recipes-title">
            {'\uD83C\uDF7D\uFE0F'} Recipes
          </h1>
          <p className="recipes-subtitle">Discover amazing recipes from our community</p>
        </div>
        <div className="header-actions">
          {user ? (
            <button 
              className="add-recipe-btn"
              onClick={() => setShowAddModal(true)}
            >
              <span>+</span>
              Add Recipe
            </button>
          ) : (
            <button 
              className="add-recipe-btn"
              onClick={() => requireAuth(user, "Register or log in to add your own recipes.")}
              style={{ backgroundColor: '#6b7280' }}
            >
              <span>{'\uD83D\uDD10'}</span>
              Add Recipe
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="search-filters-section">
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search recipes, ingredients, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {(searchTerm || selectedDifficulty || selectedCuisine || selectedTime || selectedTags.length > 0 || selectedIngredients.length > 0) && (
            <button 
              className="clear-search-btn"
              onClick={clearSearchAndFilters}
              title="Clear search and filters"
            >
              &times;
            </button>
          )}
        </div>
        <div className="filters">
          <button 
            className="filter-icon-btn"
            onClick={() => setShowFilterPopup(true)}
            title="Open filters"
          >
            <span className="filter-icon">{'\u2699\uFE0F'}</span>
            <span className="filter-text">Filter</span>
            {(selectedDifficulty || selectedCuisine || selectedTime || selectedTags.length > 0) && (
              <span className="filter-badge">{[selectedDifficulty, selectedCuisine, selectedTime, ...selectedTags].filter(Boolean).length}</span>
            )}
          </button>
          {user && (
            <>
              <label className="checkbox-filter desktop-inline-filter">
                <input
                  type="checkbox"
                  checked={showMyRecipes}
                  onChange={(e) => {
                    setShowMyRecipes(e.target.checked);
                    if (e.target.checked) {
                      setShowMyFavorites(false);
                    }
                  }}
                />
                <span className="checkbox-label">My Recipes</span>
              </label>
              <label className="checkbox-filter desktop-inline-filter">
                <input
                  type="checkbox"
                  checked={showMyFavorites}
                  onChange={(e) => {
                    setShowMyFavorites(e.target.checked);
                    if (e.target.checked) {
                      setShowMyRecipes(false);
                    }
                  }}
                />
                <span className="checkbox-label">My Favorites</span>
              </label>
              <label className="checkbox-filter desktop-inline-filter">
                <input
                  type="checkbox"
                  checked={showPantryRecipes}
                  onChange={(e) => {
                    setShowPantryRecipes(e.target.checked);
                  }}
                />
                <span className="checkbox-label">Recipes from Pantry</span>
              </label>
            </>
          )}
          <button 
            className="filter-icon-btn ingredient-filter-btn"
            onClick={() => setShowIngredientFilter(true)}
            title="Filter by ingredients"
          >
            <span className="filter-icon">{'\uD83E\uDD55'}</span>
            <span className="filter-text">Ingredients</span>
            {selectedIngredients.length > 0 && (
              <span className="filter-badge">{selectedIngredients.length}</span>
            )}
          </button>
        </div>

        {user && (
          <div className="mobile-recipe-toggles" aria-label="Recipe quick filters">
            <button
              type="button"
              className={`mobile-recipe-toggle ${showMyRecipes ? 'active' : ''}`}
              onClick={() => {
                setShowMyRecipes(!showMyRecipes);
                if (!showMyRecipes) {
                  setShowMyFavorites(false);
                }
              }}
            >
              Mine
            </button>
            <button
              type="button"
              className={`mobile-recipe-toggle ${showMyFavorites ? 'active' : ''}`}
              onClick={() => {
                setShowMyFavorites(!showMyFavorites);
                if (!showMyFavorites) {
                  setShowMyRecipes(false);
                }
              }}
            >
              Favorites
            </button>
            <button
              type="button"
              className={`mobile-recipe-toggle ${showPantryRecipes ? 'active' : ''}`}
              onClick={() => setShowPantryRecipes(!showPantryRecipes)}
            >
              Pantry
            </button>
          </div>
        )}
      </div>

      <div className="mobile-recipes-status" aria-live="polite">
        <span>{filteredRecipes.length} recipe{filteredRecipes.length === 1 ? '' : 's'}</span>
        <span>{selectedCategory === 'All' ? 'All categories' : selectedCategory}</span>
      </div>

      {/* Category Filter */}
      <div className="category-filter-section">
        <button 
          className={`category-filter-btn ${selectedCategory === 'All' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('All')}
        >
          <div className="category-icon">{'\uD83C\uDF7D\uFE0F'}</div>
          <div className="category-count">{recipes.length}</div>
          <div className="category-label">All Categories</div>
        </button>
        <button 
          className={`category-filter-btn ${selectedCategory === 'Breakfast' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('Breakfast')}
        >
          <div className="category-icon">{'\uD83C\uDF05'}</div>
          <div className="category-count">{recipes.filter(r => r.category === 'Breakfast').length}</div>
          <div className="category-label">Breakfast</div>
        </button>
        <button 
          className={`category-filter-btn ${selectedCategory === 'Lunch' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('Lunch')}
        >
          <div className="category-icon">{'\uD83E\uDD6A'}</div>
          <div className="category-count">{recipes.filter(r => r.category === 'Lunch').length}</div>
          <div className="category-label">Lunch</div>
        </button>
        <button 
          className={`category-filter-btn ${selectedCategory === 'Dinner' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('Dinner')}
        >
          <div className="category-icon">{'\uD83C\uDF7D\uFE0F'}</div>
          <div className="category-count">{recipes.filter(r => r.category === 'Dinner').length}</div>
          <div className="category-label">Dinner</div>
        </button>
        <button 
          className={`category-filter-btn ${selectedCategory === 'Snack' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('Snack')}
        >
          <div className="category-icon">{'\uD83C\uDF7F'}</div>
          <div className="category-count">{recipes.filter(r => r.category === 'Snack').length}</div>
          <div className="category-label">Snack</div>
        </button>
        <button 
          className={`category-filter-btn ${selectedCategory === 'Dessert' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('Dessert')}
        >
          <div className="category-icon">{'\uD83C\uDF70'}</div>
          <div className="category-count">{recipes.filter(r => r.category === 'Dessert').length}</div>
          <div className="category-label">Dessert</div>
        </button>
        <button 
          className={`category-filter-btn ${selectedCategory === 'Appetizer' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('Appetizer')}
        >
          <div className="category-icon">{'\uD83E\uDD57'}</div>
          <div className="category-count">{recipes.filter(r => r.category === 'Appetizer').length}</div>
          <div className="category-label">Appetizer</div>
        </button>
        <button 
          className={`category-filter-btn ${selectedCategory === 'Beverage' ? 'active' : ''}`}
          onClick={() => setSelectedCategory('Beverage')}
        >
          <div className="category-icon">{'\u2615'}</div>
          <div className="category-count">{recipes.filter(r => r.category === 'Beverage').length}</div>
          <div className="category-label">Beverage</div>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span>{'\u26A0\uFE0F'}</span>
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading recipes...</p>
        </div>
      ) : (
        /* Recipes Grid */
        <div className="recipes-grid">
          {filteredRecipes.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">{'\uD83C\uDF7D\uFE0F'}</div>
              <h3>No recipes found</h3>
              <p>Try adjusting your search or filters to find more recipes.</p>
            </div>
          ) : (
          filteredRecipes.map(recipe => (
            <div 
              key={recipe.id} 
              className="recipe-card"
              onClick={() => handleRecipeClick(recipe.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="recipe-image">
                {recipe.imageUrl ? (
                  <img src={recipe.imageUrl} alt={recipe.title} />
                ) : (
                  <div className="placeholder-image">{'\uD83C\uDF7D\uFE0F'}</div>
                )}
                <div className="recipe-actions">
                  <button 
                    className="recipe-action-btn"
                    onClick={(e) => handleShareClick(e, recipe)}
                    title="Share recipe"
                  >
                    {'\uD83D\uDCE4'}
                  </button>
                  {user && (
                    <>
                      <button 
                        className={`recipe-action-btn like-btn ${recipe.isLiked ? 'liked' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeRecipe(recipe);
                        }}
                        title={recipe.isLiked ? "Unlike recipe" : "Like recipe"}
                      >
                        {recipe.isLiked ? '\u2764\uFE0F' : '\uD83E\uDD0D'}
                      </button>
                      <button 
                        className="recipe-action-btn shopping-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateShoppingList(recipe);
                        }}
                        title="Generate shopping list"
                      >
                        {'\uD83D\uDED2'}
                      </button>
                      <button
                        className="recipe-action-btn meal-plan-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMealPlanModal(recipe);
                        }}
                        title="Add to meal plan"
                      >
                        {'\uD83D\uDCC5'}
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="recipe-content">
                <div className="recipe-header">
                  <h3 className="recipe-title">{recipe.title}</h3>
                </div>
                <p className="recipe-description">{recipe.description}</p>
                <div className="recipe-meta-info">
                  <span 
                    className="difficulty-badge"
                    style={{ backgroundColor: getDifficultyColor(recipe.difficulty) }}
                  >
                    {recipe.difficulty}
                  </span>
                  {recipe.cuisine && (
                    <span className="cuisine-badge">
                      {recipe.cuisine}
                    </span>
                  )}
                  <span className="time-badge">
                    {(recipe.prepTime || 0) + (recipe.cookTime || 0)} min
                  </span>
                  {showPantryRecipes && recipePortions[recipe.id] !== undefined && recipePortions[recipe.id] > 0 && (
                    <span className="portions-badge" title={`You can make ${recipePortions[recipe.id]} portion${recipePortions[recipe.id] !== 1 ? 's' : ''} from your pantry`}>
                      {'\uD83E\uDD6B'} {recipePortions[recipe.id]}x
                    </span>
                  )}
                </div>
                <div className="recipe-tags">
                  {recipe.tags && recipe.tags.map((tag, index) => (
                    <span key={index} className="tag">{tag}</span>
                  ))}
                  {showPantryRecipes && recipeMissingIngredients[recipe.id] && recipeMissingIngredients[recipe.id].length > 0 && (
                    <span 
                      className="missing-ingredients-badge" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShowMissingIngredients(recipe.title, recipeMissingIngredients[recipe.id]);
                      }}
                      title="Click to see missing ingredients"
                    >
                      {'\u26A0\uFE0F'} Missing {recipeMissingIngredients[recipe.id].length} ingredient{recipeMissingIngredients[recipe.id].length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                <div className="mobile-recipe-card-actions">
                  <button
                    type="button"
                    className="mobile-recipe-card-btn primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRecipeClick(recipe.id);
                    }}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="mobile-recipe-card-btn"
                    onClick={(e) => handleShareClick(e, recipe)}
                  >
                    Share
                  </button>
                  {user && (
                    <>
                      <button
                        type="button"
                        className={`mobile-recipe-card-btn ${recipe.isLiked ? 'liked' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLikeRecipe(recipe);
                        }}
                      >
                        {recipe.isLiked ? 'Saved' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="mobile-recipe-card-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateShoppingList(recipe);
                        }}
                      >
                        List
                      </button>
                      <button
                        type="button"
                        className="mobile-recipe-card-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMealPlanModal(recipe);
                        }}
                      >
                        Plan
                      </button>
                    </>
                  )}
                </div>
                
              </div>
            </div>
          ))
        )}
        </div>
      )}


      {/* Add Recipe Modal - New Design */}
      {showAddModal && (
        <div className="recipe-modal-overlay" onClick={() => {
          setShowAddModal(false);
          setUploadedImage(null);
        }}>
          <div className="recipe-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="recipe-modal-header">
              <h2 className="recipe-modal-title">ADD NEW RECIPE</h2>
              <div className="recipe-modal-subheader">
                <span className="recipe-kitchen">CREATE KITCHEN</span>
                <HelpDisclosure title="Recipe form help" items={recipeFormHelp} />
              </div>
              <button 
                className="recipe-modal-close"
                onClick={() => {
                  setShowAddModal(false);
                  setUploadedImage(null);
                }}
              >
                x
              </button>
            </div>
            <div className="recipe-modal-content">
              {hasStoredData && (
                <div className="draft-restore-notice">
                  <div className="draft-notice-content">
                    <span className="draft-icon">{'\uD83D\uDCBE'}</span>
                    <span className="draft-text">You have unsaved data from a previous session.</span>
                    <button 
                      type="button" 
                      className="clear-draft-btn"
                      onClick={clearFormData}
                    >
                      Clear Draft
                    </button>
                  </div>
                </div>
              )}
              <form id="add-recipe-form" onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                
                // Process and validate form data
                const title = formData.get('title')?.trim();
                const description = formData.get('description')?.trim();
                const ingredientsText = formData.get('ingredients')?.trim();
                const instructions = formData.get('instructions')?.trim();
                const difficulty = formData.get('difficulty');
                const cuisine = formData.get('cuisine') || null;
                const prepTime = parseInt(formData.get('prepTime')) || 0;
                const cookTime = parseInt(formData.get('cookTime')) || 0;
                const servings = parseInt(formData.get('servings')) || 4;
                // Image is completely optional - use uploaded image or URL, or null
                const urlInputValue = formData.get('imageUrl')?.trim();
                const imageUrl = uploadedImage || (urlInputValue && urlInputValue !== 'https://example.com/image.jpg' ? urlInputValue : null);
                const tagsText = formData.get('tags')?.trim();
                
                // Validate required fields
                if (!title) {
                  showSuccessMessage('Please enter a recipe title.');
                  return;
                }
                if (!description) {
                  showSuccessMessage('Please enter a recipe description.');
                  return;
                }
                if (!ingredientsText) {
                  showSuccessMessage('Please enter at least one ingredient.');
                  return;
                }
                if (!instructions) {
                  showSuccessMessage('Please enter cooking instructions.');
                  return;
                }
                if (!difficulty) {
                  showSuccessMessage('Please select a difficulty level.');
                  return;
                }
                const category = formData.get('category');
                if (!category) {
                  showSuccessMessage('Please select a category.');
                  return;
                }
                if (prepTime < 0 || cookTime < 0) {
                  showSuccessMessage('Time values cannot be negative.');
                  return;
                }
                
                // Image is optional - no validation needed
                
                // Process ingredients
                const ingredients = ingredientsText.split('\n')
                  .map(ing => ing.trim())
                  .filter(ing => ing.length > 0);
                
                if (ingredients.length === 0) {
                  showSuccessMessage('Please enter at least one valid ingredient.');
                  return;
                }
                
                // Process tags
                const tags = tagsText ? tagsText.split(',')
                  .map(tag => tag.trim())
                  .filter(tag => tag.length > 0) : [];
                
                // Process nutrition data
                const calories = parseInt(formData.get('calories')) || null;
                const fat = parseInt(formData.get('fat')) || null;
                const carbs = parseInt(formData.get('carbs')) || null;
                const protein = parseInt(formData.get('protein')) || null;
                
                const recipeData = {
                  title,
                  description,
                  ingredients,
                  instructions,
                  difficulty,
                  category,
                  cuisine,
                  prepTime,
                  cookTime,
                  servings,
                  imageUrl, // Can be uploaded image, URL, or null - all are fine
                  tags,
                  calories,
                  fat,
                  carbs,
                  protein
                };
                
                await handleAddRecipe(recipeData);
              }}>
                <div className="form-group">
                  <label>Recipe Name *</label>
                  <input 
                    type="text" 
                    name="title" 
                    placeholder="Enter recipe name" 
                    defaultValue={storedFormData.title}
                    onChange={(e) => handleFormInputChange('title', e.target.value)}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Description *</label>
                  <textarea 
                    name="description" 
                    placeholder="Describe your recipe..." 
                    defaultValue={storedFormData.description}
                    onChange={(e) => handleFormInputChange('description', e.target.value)}
                    required 
                    rows="3"
                  ></textarea>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Difficulty *</label>
                    <select 
                      name="difficulty" 
                      defaultValue={storedFormData.difficulty}
                      onChange={(e) => handleFormInputChange('difficulty', e.target.value)}
                      required
                    >
                      <option value="">Select difficulty</option>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Category *</label>
                    <select 
                      name="category" 
                      defaultValue={storedFormData.category || 'Dinner'}
                      onChange={(e) => handleFormInputChange('category', e.target.value)}
                      required
                    >
                      <option value="">Select category</option>
                      <option value="Breakfast">Breakfast</option>
                      <option value="Lunch">Lunch</option>
                      <option value="Dinner">Dinner</option>
                      <option value="Snack">Snack</option>
                      <option value="Dessert">Dessert</option>
                      <option value="Appetizer">Appetizer</option>
                      <option value="Beverage">Beverage</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Cuisine</label>
                    <select 
                      name="cuisine" 
                      defaultValue={storedFormData.cuisine}
                      onChange={(e) => handleFormInputChange('cuisine', e.target.value)}
                    >
                      <option value="">Select cuisine (optional)</option>
                      <option value="Italian">Italian</option>
                      <option value="Asian">Asian</option>
                      <option value="Mexican">Mexican</option>
                      <option value="French">French</option>
                      <option value="Indian">Indian</option>
                      <option value="Mediterranean">Mediterranean</option>
                      <option value="American">American</option>
                      <option value="German">German</option>
                      <option value="Thai">Thai</option>
                      <option value="Chinese">Chinese</option>
                      <option value="Japanese">Japanese</option>
                      <option value="Korean">Korean</option>
                      <option value="Spanish">Spanish</option>
                      <option value="Greek">Greek</option>
                      <option value="Turkish">Turkish</option>
                      <option value="Lebanese">Lebanese</option>
                      <option value="Moroccan">Moroccan</option>
                      <option value="Brazilian">Brazilian</option>
                      <option value="Peruvian">Peruvian</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Prep Time (minutes) *</label>
                    <input 
                      type="number" 
                      name="prepTime" 
                      placeholder="15" 
                      defaultValue={storedFormData.prepTime}
                      onChange={(e) => handleFormInputChange('prepTime', e.target.value)}
                      min="0" 
                      required 
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Cook Time (minutes) *</label>
                    <input 
                      type="number" 
                      name="cookTime" 
                      placeholder="30" 
                      defaultValue={storedFormData.cookTime}
                      onChange={(e) => handleFormInputChange('cookTime', e.target.value)}
                      min="0" 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Servings</label>
                    <input 
                      type="number" 
                      name="servings" 
                      placeholder="4" 
                      defaultValue={storedFormData.servings}
                      onChange={(e) => handleFormInputChange('servings', e.target.value)}
                      min="1" 
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Recipe Image (Optional)</label>
                  
                  {/* Image Upload Section */}
                  <div className="image-upload-section">
                    <div className="upload-options">
                      <div className="upload-option">
                        <label className="upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={isUploading}
                            style={{ display: 'none' }}
                          />
                          <div className="upload-button">
                            {isUploading ? (
                              <>
                                <span className="upload-icon">{'\u23F3'}</span>
                                <span>Uploading...</span>
                              </>
                            ) : (
                              <>
                                <span className="upload-icon">{'\uD83D\uDCF7'}</span>
                                <span>Upload Photo</span>
                              </>
                            )}
                          </div>
                        </label>
                        <small>Upload an image (max 5MB)</small>
                      </div>
                      
                      <div className="upload-divider">
                        <span>OR</span>
                      </div>
                      
                      <div className="upload-option">
                        <input 
                          type="url" 
                          name="imageUrl" 
                          placeholder="https://example.com/image.jpg"
                          defaultValue={storedFormData.imageUrl}
                          onChange={(e) => handleFormInputChange('imageUrl', e.target.value)}
                          disabled={!!uploadedImage}
                        />
                        <small>Enter image URL</small>
                      </div>
                    </div>
                    
                    {/* Image Preview */}
                    {uploadedImage && (
                      <div className="image-preview">
                        <img src={uploadedImage} alt="Uploaded recipe" />
                        <button 
                          type="button" 
                          className="remove-image-btn"
                          onClick={clearUploadedImage}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Ingredients (one per line) *</label>
                  <textarea 
                    name="ingredients" 
                    placeholder="2 cups flour&#10;1 cup sugar&#10;3 eggs" 
                    defaultValue={storedFormData.ingredients}
                    onChange={(e) => handleFormInputChange('ingredients', e.target.value)}
                    required 
                    rows="4"
                  ></textarea>
                  <small>Enter each ingredient on a new line</small>
                </div>
                <div className="form-group">
                  <label>Instructions *</label>
                  <textarea 
                    name="instructions" 
                    placeholder={"Preheat oven to 350 F\nMix ingredients\nBake for 25 minutes"} 
                    defaultValue={storedFormData.instructions}
                    onChange={(e) => handleFormInputChange('instructions', e.target.value)}
                    required 
                    rows="4"
                  ></textarea>
                  <small>Enter detailed cooking instructions</small>
                </div>
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input 
                    type="text" 
                    name="tags" 
                    placeholder="Italian, Pasta, Comfort Food" 
                    defaultValue={storedFormData.tags}
                    onChange={(e) => handleFormInputChange('tags', e.target.value)}
                  />
                </div>

                {/* Nutrition Information */}
                <div className="form-section">
                  <h3>Nutrition Information (Optional)</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Calories</label>
                      <input 
                        type="number" 
                        name="calories" 
                        placeholder="250" 
                        defaultValue={storedFormData.calories}
                        onChange={(e) => handleFormInputChange('calories', e.target.value)}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Fat (g)</label>
                      <input 
                        type="number" 
                        name="fat" 
                        placeholder="12" 
                        defaultValue={storedFormData.fat}
                        onChange={(e) => handleFormInputChange('fat', e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Carbs (g)</label>
                      <input 
                        type="number" 
                        name="carbs" 
                        placeholder="30" 
                        defaultValue={storedFormData.carbs}
                        onChange={(e) => handleFormInputChange('carbs', e.target.value)}
                        min="0"
                      />
                    </div>
                    <div className="form-group">
                      <label>Protein (g)</label>
                      <input 
                        type="number" 
                        name="protein" 
                        placeholder="15" 
                        defaultValue={storedFormData.protein}
                        onChange={(e) => handleFormInputChange('protein', e.target.value)}
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Serving multipliers removed: auto-scaling handles this now */}
              </form>
            </div>
            <div className="recipe-modal-footer">
              <button 
                className="recipe-cancel-btn"
                onClick={() => {
                  setShowAddModal(false);
                  setUploadedImage(null);
                }}
              >
                Cancel
              </button>
              <button 
                type="submit"
                form="add-recipe-form"
                className="recipe-submit-btn"
              >
                Add Recipe
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Popup Modal */}
      {showFilterPopup && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">FILTER RECIPES</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">RECIPE DISCOVERY</span>
                <span className="modal-context-note">Find your perfect match</span>
              </div>
              <button 
                className="ingredient-modal-close" 
                onClick={() => setShowFilterPopup(false)}
              >
                  &times;
              </button>
            </div>
            <div className="ingredient-modal-content">
              <div className="filter-sections">
                {user && (
                  <div className="filter-section">
                    <h4>Quick Filters</h4>
                    <div className="mobile-filter-toggles">
                      <label className="checkbox-filter modal-checkbox-filter">
                        <input
                          type="checkbox"
                          checked={showMyRecipes}
                          onChange={(e) => {
                            setShowMyRecipes(e.target.checked);
                            if (e.target.checked) {
                              setShowMyFavorites(false);
                            }
                          }}
                        />
                        <span className="checkbox-label">My Recipes</span>
                      </label>
                      <label className="checkbox-filter modal-checkbox-filter">
                        <input
                          type="checkbox"
                          checked={showMyFavorites}
                          onChange={(e) => {
                            setShowMyFavorites(e.target.checked);
                            if (e.target.checked) {
                              setShowMyRecipes(false);
                            }
                          }}
                        />
                        <span className="checkbox-label">My Favorites</span>
                      </label>
                      <label className="checkbox-filter modal-checkbox-filter">
                        <input
                          type="checkbox"
                          checked={showPantryRecipes}
                          onChange={(e) => {
                            setShowPantryRecipes(e.target.checked);
                          }}
                        />
                        <span className="checkbox-label">Recipes from Pantry</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Difficulty Filter */}
                <div className="filter-section">
                  <h4>Difficulty</h4>
                  <div className="filter-options">
                    <button 
                      className={`filter-option ${selectedDifficulty === '' ? 'selected' : ''}`}
                      onClick={() => setSelectedDifficulty('')}
                    >
                      All
                    </button>
                    <button 
                      className={`filter-option ${selectedDifficulty === 'easy' ? 'selected' : ''}`}
                      onClick={() => setSelectedDifficulty('easy')}
                    >
                      Easy
                    </button>
                    <button 
                      className={`filter-option ${selectedDifficulty === 'medium' ? 'selected' : ''}`}
                      onClick={() => setSelectedDifficulty('medium')}
                    >
                      Medium
                    </button>
                    <button 
                      className={`filter-option ${selectedDifficulty === 'hard' ? 'selected' : ''}`}
                      onClick={() => setSelectedDifficulty('hard')}
                    >
                      Hard
                    </button>
                  </div>
                </div>

                {/* Cuisine Filter */}
                <div className="filter-section">
                  <h4>Cuisine</h4>
                  <div className="filter-options">
                    <button 
                      className={`filter-option ${selectedCuisine === '' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('')}
                    >
                      All Cuisines
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'Italian' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('Italian')}
                    >
                      Italian
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'Asian' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('Asian')}
                    >
                      Asian
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'Mexican' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('Mexican')}
                    >
                      Mexican
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'French' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('French')}
                    >
                      French
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'Indian' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('Indian')}
                    >
                      Indian
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'Mediterranean' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('Mediterranean')}
                    >
                      Mediterranean
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'American' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('American')}
                    >
                      American
                    </button>
                    <button 
                      className={`filter-option ${selectedCuisine === 'Other' ? 'selected' : ''}`}
                      onClick={() => setSelectedCuisine('Other')}
                    >
                      Other
                    </button>
                  </div>
                </div>

                {/* Time Filter */}
                <div className="filter-section">
                  <h4>Cooking Time</h4>
                  <div className="filter-options">
                    <button 
                      className={`filter-option ${selectedTime === '' ? 'selected' : ''}`}
                      onClick={() => setSelectedTime('')}
                    >
                      All Times
                    </button>
                    <button 
                      className={`filter-option ${selectedTime === 'quick' ? 'selected' : ''}`}
                      onClick={() => setSelectedTime('quick')}
                    >
                      Quick (30 min or less)
                    </button>
                    <button 
                      className={`filter-option ${selectedTime === 'medium' ? 'selected' : ''}`}
                      onClick={() => setSelectedTime('medium')}
                    >
                      Medium (30-60 min)
                    </button>
                    <button 
                      className={`filter-option ${selectedTime === 'long' ? 'selected' : ''}`}
                      onClick={() => setSelectedTime('long')}
                    >
                      Long (&gt;60 min)
                    </button>
                  </div>
                </div>

                {/* Tags Filter */}
                <div className="filter-section">
                  <h4>Tags</h4>
                  <div className="tag-bubbles">
                    {getTopTags().map(tag => (
                      <button
                        key={tag}
                        className={`tag-bubble ${selectedTags.includes(tag) ? 'selected' : ''}`}
                        onClick={() => toggleTag(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="ingredient-modal-footer">
                <button 
                  className="ingredient-clear-btn"
                  onClick={() => {
                    setSelectedDifficulty('');
                    setSelectedCuisine('');
                    setSelectedTime('');
                    setSelectedTags([]);
                    setShowMyRecipes(false);
                    setShowMyFavorites(false);
                    setShowPantryRecipes(false);
                  }}
                >
                  Clear All
                </button>
                <button 
                  className="ingredient-apply-btn"
                  onClick={() => setShowFilterPopup(false)}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ingredient Filter Modal - New Design */}
      {showIngredientFilter && (
        <div className="ingredient-modal-overlay">
          <div className="ingredient-modal-container">
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">I WOULD LIKE...</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">QUICK KITCHEN</span>
                <span className="modal-context-note">Select ingredients to match recipes</span>
              </div>
              <button 
                className="ingredient-modal-close" 
                onClick={() => setShowIngredientFilter(false)}
              >
                &times;
              </button>
            </div>
            
            <div className="ingredient-modal-content">
              {['Proteins', 'Dairy', 'Vegetables', 'Fruits', 'Grains & Starches', 'Pantry Staples', 'Spices & Herbs', 'Canned & Jarred', 'Nuts & Seeds', 'Baking'].map(category => {
                const categoryIngredients = getIngredientList().filter(ingredient => {
                  const categoryMap = {
                    'Proteins': ['Chicken', 'Beef', 'Pork', 'Fish', 'Salmon', 'Tuna', 'Shrimp', 'Crab', 'Lobster', 'Turkey', 'Duck', 'Lamb', 'Bacon', 'Sausage', 'Ham', 'Eggs'],
                    'Dairy': ['Milk', 'Cheese', 'Cheddar', 'Mozzarella', 'Parmesan', 'Cream', 'Butter', 'Yogurt', 'Sour Cream', 'Cream Cheese'],
                    'Vegetables': ['Onions', 'Garlic', 'Tomatoes', 'Potatoes', 'Carrots', 'Broccoli', 'Spinach', 'Lettuce', 'Bell Peppers', 'Mushrooms', 'Zucchini', 'Eggplant', 'Cucumber', 'Celery', 'Cabbage', 'Cauliflower', 'Asparagus', 'Green Beans', 'Corn', 'Peas'],
                    'Fruits': ['Apples', 'Bananas', 'Oranges', 'Lemons', 'Limes', 'Berries', 'Strawberries', 'Blueberries', 'Raspberries', 'Grapes', 'Avocado', 'Pineapple', 'Mango', 'Peaches', 'Pears'],
                    'Grains & Starches': ['Rice', 'Pasta', 'Bread', 'Flour', 'Oats', 'Quinoa', 'Barley', 'Couscous', 'Noodles', 'Tortillas', 'Crackers', 'Cereal'],
                    'Pantry Staples': ['Olive Oil', 'Vegetable Oil', 'Salt', 'Pepper', 'Sugar', 'Brown Sugar', 'Honey', 'Vinegar', 'Soy Sauce', 'Worcestershire Sauce', 'Ketchup', 'Mustard', 'Mayonnaise', 'Hot Sauce', 'Sesame Oil', 'Coconut Oil'],
                    'Spices & Herbs': ['Basil', 'Oregano', 'Thyme', 'Rosemary', 'Parsley', 'Cilantro', 'Dill', 'Chives', 'Cinnamon', 'Nutmeg', 'Ginger', 'Garlic Powder', 'Onion Powder', 'Paprika', 'Cumin', 'Coriander', 'Bay Leaves', 'Red Pepper Flakes', 'Black Pepper', 'Sea Salt'],
                    'Canned & Jarred': ['Tomato Sauce', 'Tomato Paste', 'Coconut Milk', 'Chicken Broth', 'Beef Broth', 'Vegetable Broth', 'Beans', 'Chickpeas', 'Lentils', 'Olives', 'Pickles', 'Capers', 'Sun-dried Tomatoes'],
                    'Nuts & Seeds': ['Almonds', 'Walnuts', 'Pecans', 'Cashews', 'Pistachios', 'Pine Nuts', 'Sesame Seeds', 'Chia Seeds', 'Flax Seeds', 'Peanuts', 'Peanut Butter', 'Almond Butter'],
                    'Baking': ['Baking Powder', 'Baking Soda', 'Vanilla Extract', 'Chocolate', 'Cocoa Powder', 'Powdered Sugar', 'Cornstarch', 'Yeast', 'Shortening']
                  };
                  return categoryMap[category]?.includes(ingredient);
                });

                return (
                  <div key={category} className="ingredient-category-section">
                    <h3 className="ingredient-category-title">{category}</h3>
                    <div className="ingredient-list">
                      {categoryIngredients.map(ingredient => (
                        <label key={ingredient} className="ingredient-item">
                          <input
                            type="checkbox"
                            checked={selectedIngredients.includes(ingredient)}
                            onChange={() => toggleIngredient(ingredient)}
                          />
                          <span className="ingredient-name">{ingredient}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="ingredient-modal-footer">
              <button 
                className="ingredient-clear-btn"
                onClick={() => setSelectedIngredients([])}
              >
                Clear All
              </button>
              <button 
                className="ingredient-apply-btn"
                onClick={() => setShowIngredientFilter(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Achievement-Style Success Notification */}
      {showSuccess && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">{'\u2705'}</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccess(false)}>&times;</button>
          </div>
        </div>
      )}

      {showMealPlanModal && mealPlanTargetRecipe && (
        <div className="ingredient-modal-overlay" onClick={() => setShowMealPlanModal(false)}>
          <div className="ingredient-modal-container meal-plan-recipe-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">ADD TO MEAL PLAN</h2>
              <div className="ingredient-modal-subheader">
                <span className="quick-kitchen">{mealPlanTargetRecipe.title}</span>
                <span className="modal-context-note">Schedule this recipe</span>
              </div>
              <button
                className="ingredient-modal-close"
                onClick={() => setShowMealPlanModal(false)}
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleAddRecipeToMealPlan}>
              <div className="ingredient-modal-content">
                {mealPlanChoices.length > 0 ? (
                  <>
                    <div className="form-group">
                      <label>Meal Plan</label>
                      <select
                        className="form-input"
                        value={mealPlanSelection.planId}
                        onChange={(e) => setMealPlanSelection({ ...mealPlanSelection, planId: e.target.value })}
                        required
                      >
                        {mealPlanChoices.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          className="form-input"
                          value={mealPlanSelection.date}
                          onChange={(e) => setMealPlanSelection({ ...mealPlanSelection, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Meal Type</label>
                        <select
                          className="form-input"
                          value={mealPlanSelection.mealType}
                          onChange={(e) => setMealPlanSelection({ ...mealPlanSelection, mealType: e.target.value })}
                        >
                          <option value="breakfast">Breakfast</option>
                          <option value="lunch">Lunch</option>
                          <option value="dinner">Dinner</option>
                          <option value="snack">Snack</option>
                        </select>
                      </div>
                    </div>
                    <div className="recipe-plan-preview">
                      <strong>{mealPlanTargetRecipe.title}</strong>
                      <span>{mealPlanTargetRecipe.description}</span>
                    </div>
                  </>
                ) : (
                  <div className="recipe-plan-empty">
                    <h3>No meal plans yet</h3>
                    <p>Create a meal plan first, then return here to schedule recipes.</p>
                    <button type="button" className="ingredient-apply-btn" onClick={() => navigate('/meal-plans')}>
                      Go to Meal Plans
                    </button>
                  </div>
                )}
              </div>
              {mealPlanChoices.length > 0 && (
                <div className="ingredient-modal-footer">
                  <button type="button" className="ingredient-clear-btn" onClick={() => setShowMealPlanModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="ingredient-apply-btn">
                    Add to Plan
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        recipe={selectedRecipe}
      />

      {/* Shopping List Generator Modal */}
      <ShoppingListGenerator
        isOpen={showShoppingListGenerator}
        onClose={() => setShowShoppingListGenerator(false)}
        selectedRecipes={selectedRecipesForShopping}
        onShoppingListCreated={handleShoppingListCreated}
      />

      {/* Missing Ingredients Modal */}
      {showMissingIngredientsModal && (
        <div className="ingredient-modal-overlay" onClick={() => setShowMissingIngredientsModal(false)}>
          <div className="ingredient-modal-container missing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ingredient-modal-header">
              <h2 className="ingredient-modal-title">MISSING INGREDIENTS</h2>
              <p className="ingredient-modal-subtitle">{selectedMissingIngredients.recipeName}</p>
              <button 
                className="ingredient-modal-close"
                onClick={() => setShowMissingIngredientsModal(false)}
              >
                x
              </button>
            </div>
            <div className="ingredient-modal-content">
              <p className="missing-modal-info">
                You need the following ingredients to make this recipe:
              </p>
              <div className="missing-ingredients-list">
                {selectedMissingIngredients.ingredients.map((ingredient, index) => (
                  <div key={index} className="missing-ingredient-item">
                    <span className="missing-ingredient-icon">Item</span>
                    <span className="missing-ingredient-text">{ingredient}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ingredient-modal-footer">
              <button 
                className="ingredient-apply-btn"
                onClick={() => setShowMissingIngredientsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recipes;

